export interface TimezoneComboboxOption {
  value: string;
  /** From `availableTimezones`; includes GMT offset, e.g. `(GMT-03:00) Brasilia Time`. */
  label: string;
}

export interface TimezoneComboboxGroup {
  value: string;
  items: TimezoneComboboxOption[];
}

/** Maps the first segment of a backend timezone enum to a region heading. */
const TIMEZONE_REGION_GROUP_LABELS: Record<string, string> = {
  AMERICA: "Americas",
  EUROPE: "Europe",
  AFRICA: "Africa",
  ASIA: "Asia",
  AUSTRALIA: "Australia & Pacific",
  PACIFIC: "Pacific",
  ATLANTIC: "Atlantic",
  INDIAN: "Indian Ocean",
  ANTARCTICA: "Antarctica",
  ARCTIC: "Arctic",
  UTC: "UTC",
};

/**
 * Maps IANA timezone to backend enum shape (e.g. `America/Port-au-Prince` → `AMERICA_PORT_AU_PRINCE`).
 */
const formatIanaTimezoneToValue = (ianaTimezone: string): string =>
  ianaTimezone.toUpperCase().replace(/[/-]/g, "_");

/**
 * Resolves the option value for the user's local timezone, if present in `options`.
 */
export const resolveLocalTimezoneValue = (
  options: TimezoneComboboxOption[],
): string | undefined => {
  const ianaTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formatted = formatIanaTimezoneToValue(ianaTimezone);
  return options.find((option) => option.value === formatted)?.value;
};

/**
 * Groups flat timezone options by the first segment of their enum value (`AMERICA_NEW_YORK` → Americas).
 */
export const groupTimezoneOptions = (
  options: TimezoneComboboxOption[],
): TimezoneComboboxGroup[] => {
  const groups = new Map<string, TimezoneComboboxOption[]>();

  for (const option of options) {
    const regionKey = option.value.split("_")[0] ?? "OTHER";
    const groupValue = TIMEZONE_REGION_GROUP_LABELS[regionKey] ?? regionKey;
    const existing = groups.get(groupValue) ?? [];
    existing.push(option);
    groups.set(groupValue, existing);
  }

  return Array.from(groups.entries()).map(([value, items]) => ({
    value,
    items,
  }));
};
